import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@apollo/client";
import { GET_ALL_DEVICES } from "../queries/allDevicesQuery";
import { GET_LOCATIONS } from "../queries/locationQueries";
import AllDeviceTable from "../components/AllDeviceTable";
import Spinner from "../components/Spinner";
import deviceGroupService from "../services/deviceGroupService";

const TABLE_PREFS_KEY = "allDevices.tablePrefs.v1";

const loadTablePrefs = () => {
  try {
    const raw = localStorage.getItem(TABLE_PREFS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (e) {
    return null;
  }
};

const AllDevices = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const ITEMS_PER_PAGE = 100;
  const initialPrefs = useMemo(() => loadTablePrefs(), []);
  const [locationFilter, setLocationFilter] = useState(initialPrefs?.locationFilter || "");
  const [locationFilters, setLocationFilters] = useState(Array.isArray(initialPrefs?.locationFilters) ? initialPrefs.locationFilters : []);
  const [deviceTypeFilters, setDeviceTypeFilters] = useState(Array.isArray(initialPrefs?.deviceTypeFilters) ? initialPrefs.deviceTypeFilters : []);
  const [checkinRange, setCheckinRange] = useState(initialPrefs?.checkinRange || "");
  const [osVersionFilter, setOsVersionFilter] = useState(initialPrefs?.osVersionFilter || "");
  const [groupFilters, setGroupFilters] = useState(Array.isArray(initialPrefs?.groupFilters) ? initialPrefs.groupFilters : []);
  const registrations = [];
  const [groups, setGroups] = useState([]);

  const [loadingMore, setLoadingMore] = useState({
    macs: false,
    iphones: false,
    ipads: false,
    appletvs: false
  });

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  useEffect(() => {
    deviceGroupService.getDeviceGroups().then(setGroups).catch(() => setGroups([]));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        TABLE_PREFS_KEY,
        JSON.stringify({
          locationFilter,
          locationFilters,
          deviceTypeFilters,
          checkinRange,
          osVersionFilter,
          groupFilters
        })
      );
    } catch (e) {
      // ignore storage write errors
    }
  }, [locationFilter, locationFilters, deviceTypeFilters, checkinRange, osVersionFilter, groupFilters]);

  const filterVariables = (() => {
    if (locationFilters?.length) {
      return { filter: { locations: locationFilters } };
    }
    if (locationFilter) {
      return { filter: { location: locationFilter } };
    }
    return { filter: null };
  })();

  const { loading, error, data, fetchMore, refetch } = useQuery(GET_ALL_DEVICES, {
    variables: {
      first: ITEMS_PER_PAGE,
      after: null,
      ...filterVariables
    }
  });

  const { data: locationsData } = useQuery(GET_LOCATIONS);

  const loadMore = async (type) => {
    if (loadingMore[type] || !data?.[type]?.pageInfo?.hasNextPage) return;

    setLoadingMore(prev => ({ ...prev, [type]: true }));
    const endCursor = data[type].pageInfo.endCursor;

    try {
      await fetchMore({
        variables: {
          first: ITEMS_PER_PAGE,
          after: endCursor,
          ...filterVariables
        },
        updateQuery: (prevResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prevResult;

          return {
            ...prevResult,
            [type]: {
              ...fetchMoreResult[type],
              edges: [
                ...prevResult[type].edges,
                ...fetchMoreResult[type].edges
              ],
              pageInfo: fetchMoreResult[type].pageInfo,
              totalCount: fetchMoreResult[type].totalCount
            }
          };
        }
      });
    } catch (err) {
      console.error(`Error loading more ${type}:`, err);
    } finally {
      setLoadingMore(prev => ({ ...prev, [type]: false }));
    }
  };

  const combinedData = [];

  if (loading) return <Spinner />;
  if (error) return <p>Error loading devices: {error.message}</p>;

  if (data) {
    data.macs.edges.forEach(({ node }) => {
      combinedData.push({ ...node, type: "mac" });
    });
    data.iphones.edges.forEach(({ node }) => {
      combinedData.push({ ...node, type: "iphone" });
    });
    data.ipads.edges.forEach(({ node }) => {
      combinedData.push({ ...node, type: "ipad" });
    });
    data.appletvs.edges.forEach(({ node }) => {
      combinedData.push({ ...node, type: "appletv" });
    });
  }

  return (
    <AllDeviceTable
      deviceData={combinedData}
      totalCount={{
        macs: data?.macs?.totalCount,
        iphones: data?.iphones?.totalCount,
        ipads: data?.ipads?.totalCount,
        appletvs: data?.appletvs?.totalCount
      }}
      hasNextPage={{
        macs: data?.macs?.pageInfo?.hasNextPage,
        iphones: data?.iphones?.pageInfo?.hasNextPage,
        ipads: data?.ipads?.pageInfo?.hasNextPage,
        appletvs: data?.appletvs?.pageInfo?.hasNextPage
      }}
      onLoadMore={loadMore}
      loadingMore={loadingMore}
      locations={locationsData?.locations || []}
      groups={groups}
      locationFilter={locationFilter}
      locationFilters={locationFilters}
      onLocationFilterChange={setLocationFilter}
      onLocationFiltersChange={setLocationFilters}
      deviceTypeFilters={deviceTypeFilters}
      onDeviceTypeFiltersChange={setDeviceTypeFilters}
      checkinRange={checkinRange}
      onCheckinRangeChange={setCheckinRange}
      osVersionFilter={osVersionFilter}
      onOsVersionFilterChange={setOsVersionFilter}
      groupFilters={groupFilters}
      onGroupFiltersChange={setGroupFilters}
      registrations={registrations}
      refetch={refetch}
    />
  );
};

export default AllDevices;
